import type { ContenidoAyuda, ItemAyuda } from "@/ui/AyudaVista";
import {
  ATAJOS_EDITOR,
  BOTONES_CORREOS_IA,
  FICHAS_CORREOS_IA,
  botonesEditor,
} from "./redaccionComun";
import { ATAJOS_VISOR, BOTONES_VISOR } from "./visorArchivo";

/**
 * Ayuda de «Enviar correo» (`components/EnviarCorreoModal`). Lo que antes iba
 * en tooltips de sus botones, del editor y de la sección de IA.
 *
 * La ventana tiene tres modos y cada uno enseña controles distintos: al cliente
 * (`destinatario="cliente"`), reenvío de un correo (`esReenvio`) y al proveedor
 * (`destinatario="jimi"`). La IA solo aparece con `ai_comentarios_enabled`.
 */
export function ayudaEnviarCorreo({
  destinatario,
  esReenvio,
  iaHabilitada,
}: {
  destinatario: "cliente" | "jimi";
  esReenvio: boolean;
  iaHabilitada: boolean;
}): ContenidoAyuda {
  const cliente = destinatario === "cliente";
  const conOpciones = cliente && !esReenvio;

  const botones: ItemAyuda[] = [
    {
      label: "+ Agregar contacto",
      text: "Abre un buscador con los contactos del proveedor, los del cliente del caso, los asesores comerciales y el equipo interno. Si no está, escribe nombre y correo en «O escribe un email libre» y toca «+ Agregar».",
    },
    {
      label: "× en un destinatario",
      text: "Lo quita de «Para» o de «CC». No borra el contacto.",
    },
  ];
  if (cliente) {
    botones.push({
      label: "+ Agregar al asesor comercial / + Copiar al asesor comercial",
      text: "Salen cuando la empresa del cliente tiene asesor comercial y todavía no está en «Para» o en «CC»: lo agregan de un toque.",
    });
  }
  botones.push(
    {
      label: "Título de una sección (▶ / ▼)",
      text: "Abre o cierra esa sección (paquete ZIP, archivos adjuntos, asunto…). Se recuerda cómo la dejaste.",
    },
    {
      label: "Bitácora interna · ▶ mostrar",
      text: "Muestra las notas del equipo: todas las que se escribieron a mano en el caso, no solo las tuyas, para consultarlas mientras escribes. Aquí solo se leen; los cambios automáticos están en la pestaña Bitácora interna.",
    },
  );
  if (iaHabilitada) {
    botones.push(
      {
        label: "✨ Generar comentario con IA",
        text: "Escribe un borrador con las notas de la bitácora, los contactos, los equipos y los correos que marques, y lo pone en el editor. Si el editor ya tenía texto, primero pregunta («Sí, reemplazar lo de arriba»). Necesita al menos una nota escrita a mano en la bitácora. Nunca se envía solo.",
      },
      {
        label: "↑ traer lo de arriba",
        text: "Copia a «Qué quieres que diga» lo que ya escribiste en el editor, para usarlo como instrucción. Sale cuando hay texto en el editor y la instrucción está vacía.",
      },
      ...BOTONES_CORREOS_IA,
    );
  }
  if (cliente) {
    botones.push({
      label: "Borradores guardados (N) · Reusar",
      text: "Sale si el caso tiene borradores que nunca se enviaron. «Reusar» carga ese borrador en el editor y reemplaza lo que había.",
    });
  }
  botones.push(
    {
      label: "Paquete ZIP a incluir",
      text: "Elige uno de los ZIP vigentes del caso: el correo lleva su enlace y su contraseña. Los ZIP se generan en la pestaña Evidencias.",
    },
    {
      label: "+ Adjuntar archivo",
      text: "Elige archivos para adjuntar; también puedes soltarlos en cualquier parte de la ventana. Con una imagen te pregunta si va dentro del mensaje o como archivo adjunto; con uno de más de 10 MB, te ofrece mandarlo como enlace seguro de descarga. Las imágenes y los archivos pesados van de uno en uno.",
    },
    {
      label: "🖼️ Desde evidencias del caso",
      text: "Elige una evidencia del caso: si es imagen, te deja verla en el visor o insertarla; los demás archivos se adjuntan.",
    },
    { label: "✕ en un adjunto", text: "Lo quita del correo." },
    {
      label: "Asunto personalizado",
      text: "Escribe tu propio asunto. Mientras no lo toques, sigue al automático, que cambia con el tipo de correo y con «Es cierre».",
    },
    {
      label: "Fondo: Claro / Oscuro",
      text: "Solo cambia el fondo de la vista previa para leerla mejor; el correo que se manda no cambia. Se recuerda en este navegador.",
    },
    {
      label: "Cerrar caso al enviar",
      text: "Cierra el caso cuando el correo sale de verdad: con «Enviar ahora», en uno o dos minutos; programado, a esa hora. Pide el tipo de solución (por caso o por equipo). «Es cierre del caso» NO cierra nada: solo cambia el texto del correo.",
    },
    {
      label: "Cancelar",
      text: conOpciones
        ? "Cierra sin mandar. En un correo nuevo al cliente, el mensaje ya quedó guardado como borrador del caso y vuelve la próxima vez que abras esta ventana."
        : "Cierra sin mandar. Lo que escribiste se pierde.",
    },
    {
      label: "Programar envío",
      text: "Elige fecha y hora. El correo se queda en cola y lo puedes cancelar desde la pestaña Correos con «Cancelar envío».",
    },
    {
      label: "Enviar ahora",
      text: "No sale al instante: espera un minuto para que puedas tocar «Deshacer» en el aviso, y sale entre uno y dos minutos después. Sale gris si no hay nadie en «Para», si la vista previa no ha cargado o si falta el tipo de solución con «Cerrar caso al enviar».",
    },
  );

  const opciones: ItemAyuda[] = conOpciones
    ? [
        {
          label: "Tipo de correo",
          text: "«Actualización del caso» es el correo completo, con los datos del cliente y el resumen del caso. «Seguimiento simple» es un correo corto con tu mensaje, sin el resumen.",
        },
        {
          label: "¿Para quién es?",
          text: "«El cliente» le habla de «tu equipo»; «Alguien de Emprenet» habla del equipo en tercera persona. También le dice a la IA para quién redacta.",
        },
        {
          label: "Es cierre del caso",
          text: "Cambia el título y la frase del correo para avisar que el caso se cierra. No cierra el caso: para eso es «Cerrar caso al enviar».",
        },
        {
          label: "Saludar a",
          text: "Sale cuando hay dos o más personas en «Para»: elige a quién saluda el correo («Hola …»).",
        },
      ]
    : [];

  const indicadores: ItemAyuda[] = [
    {
      label: "Proveedor · Cliente · Asesor · Interno",
      text: "En la lista de «+ Agregar contacto», de dónde sale cada contacto. «principal» marca al contacto principal del proveedor o del cliente.",
    },
  ];
  if (cliente && !esReenvio) {
    indicadores.push({
      label: "Auto-guardando...",
      text: "El mensaje al cliente se está guardando como borrador del caso.",
    });
  }
  indicadores.push(
    {
      label: "Vista previa",
      text: "Así le llega al destinatario. Se actualiza sola mientras escribes o cambias opciones.",
    },
    {
      label: "Total de adjuntos (MB)",
      text: "Cuánto pesan los adjuntos contra el límite de 15 MB por correo. Se pone ámbar al pasar del 80 %.",
    },
  );
  if (iaHabilitada) indicadores.push(...FICHAS_CORREOS_IA);

  return {
    manual: "destinatarios",
    intro: (
      <p>
        {esReenvio
          ? "Reenvía el correo original tal cual, con una nota opcional arriba. A la izquierda eliges a quién va; a la derecha ves cómo le llega."
          : cliente
            ? "Arma el correo al cliente con el diseño de la empresa. A la izquierda eliges a quién va y qué dice; a la derecha ves cómo le va a llegar."
            : "Arma un correo sencillo para el proveedor de la marca: saludo, la descripción del problema y lo que tú agregues. A la derecha ves cómo le va a llegar."}{" "}
        Al enviarlo espera un minuto antes de salir, por si quieres deshacerlo.
      </p>
    ),
    secciones: [
      { titulo: "Qué hace cada botón", items: botones },
      { titulo: "Opciones del correo", items: opciones },
      { titulo: "Barra de formato del mensaje", items: botonesEditor(true) },
      { titulo: "Qué significa cada indicador", items: indicadores },
      { titulo: "Al ver una evidencia", items: BOTONES_VISOR },
      {
        titulo: "Atajos de teclado",
        items: [
          ...ATAJOS_EDITOR,
          ...ATAJOS_VISOR,
          { label: "Esc", text: "Cierra el buscador de «+ Agregar contacto»." },
          {
            label: "Enter",
            text: "En el correo de «O escribe un email libre», lo agrega a la lista.",
          },
        ],
      },
    ],
  };
}
